/**
 * Shell-level full account backup and restore.
 *
 * Reads and writes each app's storage namespace directly, bypassing the
 * per-app export/import registry (which only has the active app).
 */

import { createHybridStorage } from '@yada/storage';
import type { ExportResult, ImportResult } from '@yada/types';
import { ALL_MANIFESTS } from '../registry';

// ── Backup format ──────────────────────────────────────────────────────────────

export interface YadaBackup {
  version: '1';
  schema: 'yada-backup';
  exportedAt: string;
  settings: {
    locale?: string;
    country?: string;
  };
  apps: Record<string, Record<string, unknown>>;
  /** Present when the backup is missing data; restoring it does not restore these. */
  incomplete?: BackupGaps;
}

export interface BackupGaps {
  /** `appId/key` entries that exist but could not be read, e.g. locked documents; `appId/*` when the key list itself was unavailable. */
  unreadable: string[];
  /** Installed apps whose data this backup does not include. */
  notCovered: string[];
}

// ── App storage registry ───────────────────────────────────────────────────────
// Every installed app's namespace is backed up by listing its storage keys, so
// keys an app adds later are never silently left out. These declared keys are
// only a fallback for storage that cannot list its keys.

const APP_KEYS: Array<{ appId: string; keys?: string[] }> = [
  { appId: '@yada/app-habits',        keys: ['habits', 'events'] },
  { appId: '@yada/app-contacts',      keys: ['contacts'] },
  { appId: '@yada/app-docs',          keys: ['docs'] },
  { appId: '@yada/app-finance',       keys: ['invoices', 'templates', 'settings', 'financial_events', 'accounts'] },
  { appId: '@yada/app-shopping-list', keys: ['lists', 'settings', 'sharedLists', 'itemHistory'] },
  { appId: '@yada/app-todo',          keys: ['listIds', 'data', 'settings', 'sharedLists'] },
  { appId: '@yada/app-calendar',      keys: ['events', 'freeSlots', 'calendarRules', 'daySummaries'] },
  { appId: '@yada/app-events',        keys: ['events'] },
  { appId: '@yada/app-trips',         keys: ['trips', 'trip_legs', 'trip_participants', 'transport_offers', 'shared_requirements', 'personal_packing'] },
  { appId: '@yada/app-training',      keys: ['exercises', 'programs', 'logged_workouts', 'body_metrics', 'settings'] },
  {
    appId: '@yada/app-meal-planner',
    keys: [
      'settings', 'planned_meals', 'recipes', 'meal_backlog',
      'cravings', 'backlog_items', 'supplies', 'household_members',
      'meal_participations', 'llm_prefs',
    ],
  },
  { appId: '@yada/app-home',          keys: ['random_captures', 'events'] },
];

// ── Export ─────────────────────────────────────────────────────────────────────

export async function buildFullBackup(
  installedAppIds: string[] = ALL_MANIFESTS.map((manifest) => manifest.id),
): Promise<YadaBackup> {
  const settings: YadaBackup['settings'] = {};
  const locale = localStorage.getItem('yada:locale');
  if (locale) settings.locale = locale;
  const country = localStorage.getItem('yada:country');
  if (country) settings.country = country;

  const apps: YadaBackup['apps'] = {};
  const unreadable: string[] = [];

  const notCovered: string[] = [];
  const declared = new Map(APP_KEYS.map(({ appId, keys }) => [appId, keys ?? []]));
  const appIds = Array.from(new Set([...APP_KEYS.map(({ appId }) => appId), ...installedAppIds]));

  for (const appId of appIds) {
    const storage = createHybridStorage(appId);
    const appData: Record<string, unknown> = {};
    const declaredKeys = declared.get(appId) ?? [];

    let targetKeys = [...declaredKeys];
    let listed = false;
    if (storage.keys) {
      try {
        targetKeys = Array.from(new Set([...targetKeys, ...await storage.keys()]));
        listed = true;
      } catch {
        // Fall through: only declared keys, if any, can be read.
      }
    }
    if (!listed) {
      if (!declaredKeys.length) {
        notCovered.push(appId);
        continue;
      }
      // Only the declared keys can be read; anything else in this app is missed.
      unreadable.push(`${appId}/*`);
    }

    for (const key of targetKeys) {
      try {
        const value = await storage.get<unknown>(key);
        if (value !== null && value !== undefined) {
          appData[key] = value;
        }
      } catch {
        unreadable.push(`${appId}/${key}`);
      }
    }

    if (Object.keys(appData).length > 0) {
      apps[appId] = appData;
    }
  }

  return {
    version: '1',
    schema: 'yada-backup',
    exportedAt: new Date().toISOString(),
    settings,
    apps,
    ...(unreadable.length || notCovered.length ? { incomplete: { unreadable, notCovered } } : {}),
  };
}

export async function exportFullBackup(): Promise<ExportResult> {
  const backup = await buildFullBackup();
  const gaps = backup.incomplete;
  return {
    filename: 'yada-backup.json',
    mimeType: 'application/json',
    data: JSON.stringify(backup, null, 2),
    warnings: gaps ? [...gaps.unreadable, ...gaps.notCovered] : undefined,
  };
}

// ── Import ─────────────────────────────────────────────────────────────────────

function isValidBackup(parsed: unknown): parsed is YadaBackup {
  if (typeof parsed !== 'object' || parsed === null) return false;
  const b = parsed as Record<string, unknown>;
  return b.version === '1' && b.schema === 'yada-backup' && typeof b.apps === 'object';
}

export async function importFullBackup(data: string | Blob): Promise<ImportResult> {
  const text = typeof data === 'string' ? data : await data.text();

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { success: false, itemsImported: 0, errors: ['Invalid JSON: could not parse file'] };
  }

  if (!isValidBackup(parsed)) {
    return {
      success: false,
      itemsImported: 0,
      errors: ['Invalid backup file — expected a YADA backup with version "1" and schema "yada-backup"'],
    };
  }

  const backup = parsed;
  const failures: string[] = [];
  const conflicts: string[] = [];
  let keysRestored = 0;

  // Settings follow the same rule as data: fill what is missing, keep what is set.
  for (const [setting, value] of [['yada:locale', backup.settings.locale], ['yada:country', backup.settings.country]] as const) {
    if (!value) continue;
    const current = localStorage.getItem(setting);
    if (current === null) localStorage.setItem(setting, value);
    else if (current !== value) conflicts.push(`settings/${setting.slice('yada:'.length)}`);
  }

  // Restore app data (support all keys present in backup.apps)
  const appIdsToRestore = Array.from(new Set([
    ...APP_KEYS.map(k => k.appId),
    ...Object.keys(backup.apps),
  ]));

  for (const appId of appIdsToRestore) {
    const appData = backup.apps[appId];
    if (!appData) continue;

    const storage = createHybridStorage(appId);

    for (const [key, value] of Object.entries(appData)) {
      if (value === undefined) continue;
      try {
        // Never overwrite local data: restore only what is missing, and report a
        // differing local value as a conflict instead of replacing it.
        const existing = await storage.get<unknown>(key);
        if (existing === null || existing === undefined) {
          await storage.set(key, value);
          keysRestored++;
        } else if (!sameValue(existing, value)) {
          conflicts.push(`${appId}/${key}`);
        }
      } catch (err) {
        failures.push(`${appId}/${key}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  }

  if (failures.length || conflicts.length) {
    return {
      success: false,
      itemsImported: keysRestored,
      errors: [
        `Restore incomplete: ${keysRestored} item(s) restored.`,
        ...failures.map((failure) => `Could not restore ${failure}`),
        ...conflicts.map((entry) => `Kept the existing local value of ${entry}; the backup's differs and was not applied`),
      ],
    };
  }

  return { success: true, itemsImported: keysRestored };
}

function sameValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  return aKeys.length === bKeys.length &&
    aKeys.every((key) => sameValue((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
}
